import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import debounce from 'lodash.debounce'

// Components
import StatusTag from '../tag/StatusTag'
import CultureVisualisation, { IHabit } from '../CultureVisualisation'
import Icon from '../icon/Icon'

// Utils
import { useAppState } from '../../context/AppContext'
import { notEmpty } from '../../utils/typeGuards'
import { cultureValueColors } from '../../utils/habitHelper'
import getInsightsTrends from '../../context/services/insights/getInsightsTrends.service'

// Styles
import { createColor, hsla } from '../../styles/colors'
import { applyTextStyle } from '../../styles/typography'

// Types
import { ICompanyTerm } from '../../common/models/Company'
import {
  IInsightsCultureValue,
  IInsightsTrends,
} from '../../common/models/Insights'

interface ITermTrends extends IInsightsTrends {
  term: ICompanyTerm
}

interface IProps {
  selectedTerm: ICompanyTerm
  compareTo?: ICompanyTerm
  memberIds?: string[]
}

// Key idea: this strip is *historical*. Both the list of values and their colors
// come from a per-term snapshot returned by the trends endpoint, NOT from the
// live company. A value that has since been renamed, recolored or deleted still
// renders here exactly as it was during the selected term. That is why colors
// are rebuilt from each value's stored HSL (see colorsFor) rather than looked up
// on the live theme, and why scores are read from the snapshot's feedbackCircles.
const CultureStrip: React.FC<IProps> = ({
  selectedTerm,
  compareTo,
  memberIds,
}) => {
  const { company, theme, userAuthToken } = useAppState()

  const [selectedTermTrends, setSelectedTermTrends] = useState<ITermTrends>()
  const [compareToTrends, setCompareToTrends] = useState<ITermTrends>()
  const [loadedMemberKey, setLoadedMemberKey] = useState<string>()

  // Stable signature of a member selection: sorted ids joined
  const memberKey = (ids?: string[]) => [...(ids || [])].sort().join(',')

  // Fetch the per-term trends snapshot (culture values + feedback circles) for a
  // single term, tagging the result with the term it belongs to. `members`
  // optionally scopes the averages to the currently filtered member set.
  const loadTrends = async (
    term: ICompanyTerm,
    setState: React.Dispatch<React.SetStateAction<ITermTrends | undefined>>,
    members?: string[]
  ) => {
    try {
      if (!userAuthToken) return
      setState({
        ...(await getInsightsTrends(
          term.termNumber,
          userAuthToken || '',
          members
        )),
        term,
      })
      setLoadedMemberKey(memberKey(members))
    } catch (e) {
      //
    }
  }

  // Debounced loader: waits for input to settle, then fetches the selected term
  // first and the comparison term after it. Debouncing collapses the burst of
  // calls that the member filter fires while the user is still adjusting it.
  // Memoized with empty deps so the same debounced instance lives for the
  // component's lifetime (re-creating it would reset its internal timer).
  const loadDebounced = useMemo(
    () =>
      debounce(
        (selTerm, setSelTerm, compTerm, setCompTerm, members) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          ;(async () => {
            if (!selTerm) return
            await loadTrends(selTerm, setSelTerm, members)
          })().then(async () => {
            if (!compTerm) return
            await loadTrends(compTerm, setCompTerm, members)
          })
        },
        500,
        {
          leading: false,
          trailing: true,
        }
      ),
    []
  )

  // (Re)load only when something that affects the snapshot actually changed:
  // nothing loaded yet, the selected or comparison term switched, or the member
  // selection changed. The guard prevents redundant refetches on unrelated
  // re-renders.
  useEffect(() => {
    if (
      (selectedTerm || compareTo) &&
      ((!selectedTermTrends && !compareToTrends) ||
        selectedTermTrends?.term.termNumber !== selectedTerm.termNumber ||
        compareToTrends?.term.termNumber !== compareTo?.termNumber ||
        loadedMemberKey !== memberKey(memberIds))
    ) {
      loadDebounced(
        selectedTerm,
        setSelectedTermTrends,
        compareTo,
        setCompareToTrends,
        memberIds
      )
    }
  }, [memberIds, userAuthToken, selectedTerm, compareTo])

  if (!company || company.cultureValues.length === 0) return null

  // Render the values as they were during the selected term (union across the
  // term's company versions, provided by the trends endpoint). Fall back to the
  // live company values until the trends request resolves, so the strip never
  // flashes empty.
  const displayValues: IInsightsCultureValue[] =
    selectedTermTrends?.cultureValues ??
    company.cultureValues.map((value) => ({
      cultureValueId: value._id,
      subtitle: value.subtitle,
      color: value.color,
      colorHsl: company.companyColors[value.color],
    }))

  if (displayValues.length === 0) return null

  // Split into balanced rows of at most 5: rows = ceil(n / 5), sizes as even as
  // possible (the first `extra` rows get one more). e.g. 7 -> 4+3, 13 -> 5+4+4.
  const rowCount = Math.ceil(displayValues.length / 5)
  const base = Math.floor(displayValues.length / rowCount)
  const extra = displayValues.length % rowCount
  const rows: IInsightsCultureValue[][] = []
  let cursor = 0
  for (let row = 0; row < rowCount; row++) {
    const size = base + (row < extra ? 1 : 0)
    rows.push(displayValues.slice(cursor, cursor + size))
    cursor += size
  }

  // Build every shade a card needs from the value's stored HSL, so a value that
  // no longer exists on the live company/theme still renders in its original color.
  const colorsFor = (color: string, hsl?: number[]) => {
    const colors = cultureValueColors(theme, color, undefined, hsl)
    const fillColor = hsl
      ? createColor(hsl)
      : theme.colors[color] || theme.colors.primary
    return {
      ...colors,
      fillColor,
      mediumValueColor: colors.mediumValueColor || hsla(fillColor, 0.5),
    }
  }

  return (
    <StripWrapper>
      {rows.map((rowValues, rowIndex) => (
        <CardRow key={`culture-row-${rowIndex}`}>
          {rowValues.map((value, indexInRow) => {
            const {
              fillColor,
              lightValueColor,
              darkValueColor,
              mediumValueColor,
            } = colorsFor(value.color, value.colorHsl)

            const selectedCircleValue = selectedTermTrends?.feedbackCircles.find(
              (circle) => circle.cultureValueId === value.cultureValueId
            )?.averageCultureValue

            const comparedCircleValue = compareToTrends?.feedbackCircles.find(
              (circle) => circle.cultureValueId === value.cultureValueId
            )?.averageCultureValue

            const trend =
              typeof selectedCircleValue === 'number' &&
              typeof comparedCircleValue === 'number'
                ? selectedCircleValue > comparedCircleValue
                  ? 'up'
                  : selectedCircleValue === comparedCircleValue
                  ? 'same'
                  : 'down'
                : undefined

            return (
              <ValueCard bgColor={lightValueColor} key={value.cultureValueId}>
                <StyledTag
                  caption={value.subtitle}
                  captionColor={lightValueColor}
                  background={darkValueColor}
                />
                <StyledCultureVisualisation
                  zIndex={rowValues.length - indexInRow}
                  activeHabit={-1}
                  definedSize={128}
                  definedStrokeWidth={12}
                  definedImageSize={20}
                  foreignCenterObject={
                    trend ? (
                      <StyledIcon
                        type={trend === 'same' ? 'Dash' : 'ArrowRight'}
                        size={20}
                        color={darkValueColor}
                        arrowDirection={trend === 'same' ? undefined : trend}
                      />
                    ) : undefined
                  }
                  habits={
                    [
                      {
                        color: fillColor,
                        backgroundColor: theme.colors.lightest,
                        value:
                          selectedCircleValue === 0
                            ? 10
                            : selectedCircleValue || 0, // pass 10 when value = 0 just to start filling the circle
                        hoverText:
                          selectedCircleValue != null
                            ? `${selectedTerm.name} · ${Math.round(
                                selectedCircleValue
                              )}`
                            : selectedTerm.name,
                      },
                      compareTo
                        ? {
                            color: mediumValueColor,
                            backgroundColor: theme.colors.lightest,
                            value:
                              comparedCircleValue === 0
                                ? 10 // pass 10 when value = 0 just to start filling the circle
                                : comparedCircleValue || 0,
                            hoverText:
                              comparedCircleValue != null
                                ? `${compareTo.name} · ${Math.round(
                                    comparedCircleValue
                                  )}`
                                : compareTo.name,
                          }
                        : undefined,
                    ].filter((habit) => notEmpty(habit)) as IHabit[]
                  }
                />
              </ValueCard>
            )
          })}
        </CardRow>
      ))}
    </StripWrapper>
  )
}

export default CultureStrip

const StripWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CardRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

// Cards grow to fill their row. The min-width lets a row wrap onto extra lines on
// narrow screens instead of squashing the circles.
const ValueCard = styled.div<{ bgColor: string }>`
  flex: 1 1 176px;
  min-width: 176px;
  padding: 24px;
  border-radius: 12px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledTag = styled(StatusTag)<{ background: string }>`
  background-color: ${({ background }) => background};
  margin-bottom: 24px;
  padding: 4px 12px;
  ${applyTextStyle('labelSmallCaps')}
`

const StyledCultureVisualisation = styled(CultureVisualisation)<{
  zIndex: number
}>`
  z-index: ${({ zIndex }) => zIndex};
`

const StyledIcon = styled(Icon)<{ arrowDirection?: 'up' | 'down' }>`
  ${({ arrowDirection }) =>
    arrowDirection &&
    `
    transform: rotate(${arrowDirection === 'up' ? -45 : 45}deg);
  `}
`
